import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { StorageKeys, StringOrNull } from '@core-interfaces/global';
import type { IAuthService, IloginFormInput } from '@core-services/auth';
import type { IActiveUserSession } from '@core-ports/outputs/session';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  IGenerateOtpWithEmailResponse,
  ILoginResponse,
  IResetPasswordResponse,
  IValidateOtpResponse,
} from '@core-ports/outputs/login';
import { Observable, map } from 'rxjs';
import { Apollo } from 'apollo-angular';
import {
  generate_otp_with_email,
  login_admin,
  reset_password,
  validate_otp,
} from '@infra-adapters/graphql/Auth';
import { ILoginInput, IOtpValidate } from '@core-ports/inputs/login';
@Injectable({
  providedIn: 'root',
})
export class AuthManager implements IAuthService {
  loginForm: FormGroup<IloginFormInput>;

  constructor(private apollo: Apollo) {
    this.loginForm = new FormGroup<IloginFormInput>({
      email: new FormControl<StringOrNull>(null, {
        validators: [Validators.required, Validators.email],
      }),
      password: new FormControl<StringOrNull>(null, {
        validators: [Validators.required],
      }),
    });
  }

  isAuth(): boolean {
    const sessionToken = sessionStorage.getItem(StorageKeys.session_token);
    return sessionToken && sessionToken.length > 0 ? true : false;
  }

  UserSessionData(): IActiveUserSession | null {
    const sessionToken = sessionStorage.getItem(StorageKeys.session_token);
    if (!sessionToken) {
      return null;
    }
    return jwtDecode<IActiveUserSession>(sessionToken);
  }

  setSession(token: string): void {
    sessionStorage.setItem(StorageKeys.session_token, token);
  }

  cleanSession(): void {
    sessionStorage.removeItem(StorageKeys.session_token);
  }

  login(input: ILoginInput): Observable<ILoginResponse> {
    return this.apollo
      .mutate<ILoginResponse>({
        mutation: login_admin,
        variables: {
          input,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((response) => {
          if (response.data?.auth_admin) {
            return response.data;
          } else {
            throw new Error('Error al iniciar sesión');
          }
        })
      );
  }

  generateOtpWithEmail(email: string): Observable<string> {
    return this.apollo
      .mutate<IGenerateOtpWithEmailResponse>({
        mutation: generate_otp_with_email,
        variables: {
          email,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((response) => {
          if (response.data?.generateOtpWithEmail) {
            return response.data.generateOtpWithEmail;
          } else {
            throw new Error('Error al generar OTP');
          }
        })
      );
  }

  validateOtp(input: IOtpValidate): Observable<string> {
    return this.apollo
      .mutate<IValidateOtpResponse>({
        mutation: validate_otp,
        variables: {
          input,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((response) => {
          if (response.data?.validateOtp) {
            return response.data.validateOtp;
          } else {
            throw new Error('Error al validar OTP');
          }
        })
      );
  }

  resetPassword(password: string, reset_token: string): Observable<string> {
    return this.apollo
      .mutate<IResetPasswordResponse>({
        mutation: reset_password,
        variables: {
          password,
        },
        fetchPolicy: 'network-only',
        context: {
          headers: {
            Authorization: `Bearer ${reset_token}`,
          },
        },
      })
      .pipe(
        map((response) => {
          if (response.data?.resetPassword) {
            return response.data.resetPassword;
          } else {
            throw new Error('Error al resetear contraseña');
          }
        })
      );
  }
}
